import { useCallback, useEffect, useState } from 'react';
import { useBeforeUnload, useBlocker, useLocation } from 'react-router-dom';
import { downloadResultsLog, getResultsLog, saveResultsLog } from '../../API/resultsLog';
import './style.scss';

const EMPTY_FIELDS = {
  initialObservations: '',
  socioculturalReflection: '',
  transformationObservations: ''
};

const QUESTIONS = [
  {
    field: 'initialObservations',
    title: '1. Descripción del grupo y observaciones de proceso inicial',
    guidance: '¿Cómo se observa el grupo en el primer segmento del proceso (1ra–2da sesión)? Capacidad de expresión, escucha, disposición para la participación, relacionamiento, conocimiento y percepción sobre su espacio y su realidad circundante, interés y prácticas de participación, testimonios de conexión entre identidad, vínculos y entorno en el que viven, entre otros, descritos en los observables de la matriz.'
  },
  {
    field: 'socioculturalReflection',
    title: '2. Reflexión sobre los aspectos socioculturales del grupo y el nivel de fortalecimiento de capacidades',
    guidance: 'Capacidades: imaginación, juicio crítico, praxis ética, afiliación y control sobre el propio entorno (3ra a 6ta sesión). Reconocimiento de los liderazgos, actores, escenarios, experiencias, acciones y prácticas de participación. Identificación de atributos, preguntas y reflexiones territoriales. Uso de herramientas de lectura territorial, autorreconocimiento y compromiso de participación; definición de la situación a intervenir; desarrollo de ideas y consensos y concertación de acciones para incidir en su realidad.'
  },
  {
    field: 'transformationObservations',
    title: '3. Otras observaciones particulares que evidencian transformación',
    guidance: 'Cambios actitudinales, en el estado emocional, situaciones diversas, aspectos positivos, lecciones aprendidas y casos exitosos, entre otros.'
  }
];

const AGE_GROUPS = [
  ['earlyChildhood', 'Primera infancia', '0–5 años'],
  ['childrenAdolescents', 'Niñez y adolescencia', '6–17 años'],
  ['youth', 'Juventud', '18–28 años'],
  ['adults', 'Adulto/a', '29–54 años'],
  ['olderAdults', 'Adulto mayor', '55 años o más']
];

function pickFields(log) {
  return Object.fromEntries(Object.keys(EMPTY_FIELDS).map(key => [key, log?.[key] || '']));
}

function errorMessage(error, saving) {
  const code = error?.response?.data?.message;
  if (code === 'RESULTS_LOG_CONFLICT') {
    return 'La bitácora cambió en otra sesión. Tus respuestas siguen aquí: cópialas antes de recargar la página para consultar la última versión.';
  }
  if (error?.response?.status === 403) return 'No tienes permiso para acceder a la bitácora de este grupo.';
  if (code === 'RESULTS_LOG_UNSUPPORTED_PROCESS') return 'La bitácora está disponible para los procesos NDN y PF.';
  return saving
    ? 'No se pudieron guardar los avances. Tus respuestas siguen en el formulario; intenta guardarlas de nuevo.'
    : 'No se pudo cargar la bitácora. Intenta de nuevo.';
}

function ResultsLogForm({ groupId }) {
  const [header, setHeader] = useState(null);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [savedFields, setSavedFields] = useState(EMPTY_FIELDS);
  const [revision, setRevision] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const dirty = Object.keys(EMPTY_FIELDS).some(key => fields[key] !== savedFields[key]);
  const blocker = useBlocker(dirty || saving);

  useBeforeUnload(useCallback(event => {
    if (dirty || saving) {
      event.preventDefault();
      event.returnValue = '';
    }
  }, [dirty, saving]));

  useEffect(() => {
    if (!groupId) {
      setLoadError('Selecciona un grupo desde la lista de encuentros para abrir su bitácora.');
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setLoadError('');
    getResultsLog(groupId).then(data => {
      if (!active) return;
      setHeader(data.header);
      setFields(pickFields(data.log));
      setSavedFields(pickFields(data.log));
      setRevision(data.log.revision);
      setUpdatedAt(data.log.updatedAt);
    }).catch(error => {
      if (active) setLoadError(errorMessage(error, false));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [groupId, loadAttempt]);

  async function handleSave(event) {
    event.preventDefault();
    if (saving || !header || !dirty) return;
    setSaving(true);
    setSaveError('');
    try {
      const data = await saveResultsLog(groupId, { ...fields, revision });
      setHeader(data.header);
      setSavedFields(pickFields(data.log));
      setFields(pickFields(data.log));
      setRevision(data.log.revision);
      setUpdatedAt(data.log.updatedAt);
    } catch (error) {
      setSaveError(errorMessage(error, true));
    } finally {
      setSaving(false);
    }
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setFields(current => ({ ...current, [name]: value }));
  }

  async function handleDownload() {
    if (downloading || saving || dirty || !revision) return;
    setDownloading(true);
    setDownloadError('');
    try {
      const blob = await downloadResultsLog(groupId, revision);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `BitResul_${String(header.groupCode).replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setDownloadError(error?.response?.data?.message === 'RESULTS_LOG_CONFLICT'
        ? 'La bitácora cambió en otra sesión. Recarga la página para descargar la versión más reciente.'
        : 'No se pudo descargar el PDF. Intenta de nuevo.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section className='results-log' aria-labelledby='results-log-title'>
      <header className='results-log__header'>
        <span className='material-symbols-rounded results-log__icon' aria-hidden='true'>menu_book</span>
        <div>
          <h1 id='results-log-title'>Bitácora de resultados</h1>
          {header && <p className='results-log__context'>Grupo {header.groupCode}</p>}
        </div>
      </header>

      {loading && <p role='status'>Cargando bitácora…</p>}
      {loadError && (
        <div className='results-log__content'>
          <p role='alert'>{loadError}</p>
          {groupId && <button type='button' onClick={() => setLoadAttempt(value => value + 1)}>Reintentar</button>}
        </div>
      )}

      {!loading && !loadError && header && (
        <form onSubmit={handleSave}>
          <div className='results-log__content'>
            <p className='results-log__guidance'>
              <strong>Orientaciones: </strong>
              Este registro permite anotar cotidianamente las observaciones que tiene el mediador
              respecto a cada uno de los grupos participantes, a través de las diferentes sesiones
              del proceso, a fin de plasmar su evolución y los resultados, desde un ejercicio de
              reflexión crítica. Se basa en los observables planteados en la Matriz Formativa.
            </p>
            <dl className='results-log__summary'>
              <div><dt>Grupo/organización o institución</dt><dd>{header.groupCode}</dd></div>
              <div><dt>Comuna</dt><dd>{header.commune || 'Sin registrar'}</dd></div>
              <div><dt>N.º de participantes</dt><dd>{header.participantCount}</dd></div>
              <div><dt>Lugar</dt><dd>{header.place || 'Sin registrar'}</dd></div>
              <div><dt>Mediador</dt><dd>{header.mediatorName}</dd></div>
              <div>
                <dt>Período de registro (hora de Bogotá)</dt>
                <dd>{updatedAt ? new Date(updatedAt).toLocaleString('es-CO', { timeZone: 'America/Bogota' }) : 'Se asigna al guardar los avances'}</dd>
              </div>
            </dl>
            <h2>Grupo etario</h2>
            <dl className='results-log__ages'>
              {AGE_GROUPS.map(([key, label, range]) => (
                <div key={key}>
                  <dt>{label}<small>{range}</small></dt>
                  <dd>{header.ageGroups[key]}</dd>
                </div>
              ))}
            </dl>
            <p className='results-log__hint'>
              Calculado con los beneficiarios activos registrados en el grupo.
              {header.ageGroups.unknown > 0 && ` Sin edad registrada válida: ${header.ageGroups.unknown}.`}
            </p>
          </div>

          <fieldset className='results-log__fields' disabled={saving || downloading}>
            <legend className='results-log__legend'>Registro y observaciones</legend>
            {QUESTIONS.map(question => (
              <div className='results-log__content' key={question.field}>
                <label htmlFor={question.field}>{question.title}</label>
                <p id={`${question.field}-help`} className='results-log__guidance'>{question.guidance}</p>
                <textarea id={question.field} name={question.field} rows={8}
                  aria-describedby={`${question.field}-help`} value={fields[question.field]} onChange={handleChange} />
              </div>
            ))}
          </fieldset>

          <div className='results-log__save'>
            <div>
              <p className='results-log__hint'>Puedes guardar respuestas parciales y continuar después. Los campos no tienen límite de caracteres.</p>
              <p role='status' aria-live='polite'>
                {saving ? 'Guardando…' : dirty ? 'Tienes cambios sin guardar.' : updatedAt
                  ? `Avances guardados. Última actualización (Bogotá): ${new Date(updatedAt).toLocaleString('es-CO', { timeZone: 'America/Bogota' })}`
                  : 'Aún no has guardado avances.'}
              </p>
              {saveError && <p role='alert' className='results-log__error'>{saveError}</p>}
              {downloadError && <p role='alert' className='results-log__error'>{downloadError}</p>}
              {(dirty || !revision) && <p id='results-log-pdf-help' className='results-log__hint'>Guarda los avances para descargar el PDF actualizado.</p>}
            </div>
            <div className='results-log__actions'>
              <button type='submit' disabled={saving || downloading || !dirty}>{saving ? 'Guardando…' : 'Guardar avances'}</button>
              <button type='button' className='results-log__download' onClick={handleDownload}
                aria-describedby={dirty || !revision ? 'results-log-pdf-help' : undefined}
                disabled={saving || downloading || dirty || !revision}>
                {downloading ? 'Generando PDF…' : 'Descargar PDF'}
              </button>
            </div>
          </div>
        </form>
      )}

      {blocker.state === 'blocked' && (
        <div className='results-log__overlay'>
          <section className='results-log__dialog' role='alertdialog' aria-modal='true'
            aria-labelledby='leave-log-title' aria-describedby='leave-log-description'>
            <h2 id='leave-log-title'>Cambios sin guardar</h2>
            <p id='leave-log-description'>Guarda los avances antes de salir para poder retomarlos después.</p>
            <div className='results-log__dialog-actions'>
              <button type='button' autoFocus onClick={() => blocker.reset()}>Seguir editando</button>
              <button type='button' disabled={saving} onClick={() => blocker.proceed()}>Salir sin guardar</button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

export default function ResultsLog() {
  const location = useLocation();
  const stateGroup = location.state?.group;
  const rawId = new URLSearchParams(location.search).get('groupId') ||
    (typeof stateGroup === 'object' ? stateGroup?.id : stateGroup);
  const groupId = Number.isSafeInteger(Number(rawId)) && Number(rawId) > 0 ? Number(rawId) : null;
  return <ResultsLogForm key={groupId || 'missing'} groupId={groupId} />;
}
